import React from "react";
import { css } from "@emotion/react";
import { camelize } from "humps";
import { useTranslation } from "react-i18next";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { Cruise } from "main/javascripts/types/cruise";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { nl2br } from "main/javascripts/utils/StringUtil";
import { borderRadius } from "../../styles/base/borderStyle";

export interface IProps {
  attractions: Cruise.VesselAttraction[];
  category: string;
}

export const CruiseVesselAttractionList: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { attractions, category } = props;
  const { t } = useTranslation(["component", "constant", "button"]);

  const maxInitialItemNum = 6;

  const [showAll, setShowAll] = React.useState(false);

  const categoryAttractions = attractions
    .filter((attraction) => attraction.category === category)
    .sort((a, b) => {
      return (b.includedInFare ? 1 : 0) - (a.includedInFare ? 1 : 0);
    });

  if (categoryAttractions.length === 0) {
    return null;
  }

  const buttonText = !showAll
    ? t("button:common.showAll")
    : t("button:common.showLess");

  const onClick = () => {
    setShowAll(!showAll);
  };

  return (
    <div css={attractionListStyle}>
      {categoryAttractions.map((attraction, index) => {
        if (!showAll && index >= maxInitialItemNum) {
          return null;
        }
        return (
          <div css={attractionItemStyle} key={attraction.id}>
            <div css={imageBlockStyle}>
              {attraction.imageUrl && (
                <img css={imageStyle} src={attraction.imageUrl} alt="" />
              )}
            </div>
            <div css={dataBlockStyle}>
              <div css={titleStyle}>{attraction.title}</div>
              {attraction.includedInFare ? (
                <div css={includedInFareStyle}>
                  {t("cruise.includedInFare")}
                </div>
              ) : (
                <div css={additionalChargeStyle}>
                  {t("cruise.additionalCharge")}
                </div>
              )}
              <div css={detailBlockStyle}>
                {attraction.detail && <div>{nl2br(attraction.detail)}</div>}
                {(attraction.ageRange ||
                  attraction.attire ||
                  attraction.cuisineType) && (
                  <div>
                    {attraction.ageRange && <div>{attraction.ageRange}</div>}
                    {attraction.attire && (
                      <div>
                        {t(
                          `constant:vesselAttractionAttire.${camelize(
                            attraction.attire
                          )}`
                        )}
                      </div>
                    )}
                    {attraction.cuisineType && (
                      <div>
                        {t(
                          `constant:vesselAttractionCuisineType.${camelize(
                            attraction.cuisineType
                          )}`
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {categoryAttractions.length > maxInitialItemNum && (
        <div css={buttonBlockStyle}>
          <a css={buttonStyle} onClick={onClick}>
            {buttonText}
          </a>
        </div>
      )}
    </div>
  );
};

const attractionListStyle = css`
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space.atom2x};
`;
const attractionItemStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: ${space.atom3x};
`;
const imageBlockStyle = css`
  aspect-ratio: 2 / 1;
  border-radius: ${borderRadius.normal};
  overflow: hidden;
`;
const imageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const dataBlockStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const titleStyle = css`
  font-size: ${fontSize.body};
  font-weight: ${fontWeight.bold};
  line-height: ${lineHeight.body2};
  padding: 0;
`;
const includedInFareStyle = css`
  color: ${textColor.secondaryAccentColor};
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
`;
const additionalChargeStyle = css`
  color: ${textColor.primaryAccentColor};
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
`;
const detailBlockStyle = css`
  padding: 0 0 ${space.atom};
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
`;
const buttonBlockStyle = css`
  display: flex;
  justify-content: flex-start;
  padding: ${space.atom1_5x} 0 0;
`;
const buttonStyle = css`
  color: ${accentColor.primaryColor};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.5rem;
`;
